import { BackButton } from "components/parts/BackButton";
import { TextWithIconButton } from "components/parts/Button/TextWithIconButton";
import { TextWithIconTypes } from "components/parts/Button/TextWithIconButton/TextWithIconButton";
import { StatusLabel } from "components/parts/Label/StatusLabel";
import { Typography } from "components/parts/Typography/Typography";
import { useExcelJS } from "hooks/useExcelJS";
import { usePageHeader } from "hooks/usePageHeader";
import { InterviewDetail } from "pages/InterviewManagement/interviewDetail";
import { InterviewList } from "pages/InterviewManagement/InterviewList";
import { InterviewManageModal } from "pages/InterviewManagement/InterviewManageModal";
import { useConsoleState } from "providers/ConsoleStateProvider";
import React from "react";
import { useHistory } from "react-router";
import { Status } from "types/status";

import { DisplayInterview, useInterviewList } from "./hook";
import {
  StyledButtonRow,
  StyledButtonRowLeft,
  StyledContentWrapper,
  StyledGroupAndSubjectName,
  StyledGroupColumn,
  StyledHeadline,
  StyledInterviewDetailContainer,
  StyledInterviewListContainer,
  StyledInterviewManagementContainer,
  StyledSubjectNameContainer,
} from "./styled";
import { InterviewType } from "types/graphqlapi";
import { InterviewTypeIcon } from "components/parts/InterviewTypeIcon";
import { listLastMonitorSentDateListQuery } from "utils/apis/monitor";
import { useLoadingState } from "providers/LoadingProvider";

export type InterviewManagementParams = {
  subject_id: string;
};

export const InterviewManagement: React.FC = () => {
  const history = useHistory();
  const {
    selectedGroupName,
    selectedSubjectId,
    selectedSubjectStatus,
    selectedSubjectType,
  } = useConsoleState();
  const { setLoading } = useLoadingState();
  const { pageHeader } = usePageHeader(selectedSubjectId);
  const { displayInterviewList, fetchInterviews } =
    useInterviewList(selectedSubjectId);

  const [selectedInterview, setSelectedInterview] =
    React.useState<DisplayInterview>();

  const { downloadExcel } = useExcelJS();

  const selectInterview = (interview: DisplayInterview) => {
    setSelectedInterview(interview);
  };

  const [selectedInterviewDetail, setSelectedInterviewDetail] =
    React.useState<DisplayInterview>();

  // モニターの最終投稿日時取得
  const selectInterviewDetail = async (
    interview: DisplayInterview | undefined
  ) => {
    if (!interview) return;

    const needApiRequest =
      interview.monitorList.length !== 0 && !interview.monitorSentRequested;
    if (needApiRequest) {
      setLoading(true);
      try {
        interview.monitorSentRequested = true;

        const lastMonitorSendDateList = await listLastMonitorSentDateListQuery(
          interview.id,
          interview.monitorList
        );
        if (!lastMonitorSendDateList) return;

        const withSentDateMonitorList = interview.monitorList.map((monitor) => {
          const targetSentDate = lastMonitorSendDateList.find(
            (sent) => sent.monitor_id === monitor.id
          )?.createdAt;
          return { ...monitor, lastSentDate: targetSentDate };
        });

        interview.monitorList = withSentDateMonitorList;
      } catch (e) {
        console.error(e);
        alert("エラーが発生しました。");
        history.replace("/auth/login");
      } finally {
        setLoading(false);
      }
    }
    setSelectedInterviewDetail(interview);
  };

  const createNewInterview = () => {
    selectInterview({} as DisplayInterview);
  };

  const closeSelectedInterview = () => {
    setSelectedInterview(undefined);
  };

  // 新規作成、更新、削除のタイミングで使う
  const clearSelectedInterview = () => {
    setSelectedInterview(undefined);
    fetchInterviews();
  };

  const moveReport = () => {
    history.replace(`/report`);
  };

  const moveScenarioManage = () => {
    history.replace(`/scenario`);
  };

  const moveFileManage = () => {
    history.replace(`/file`);
  };

  const moveFileDL = () => {
    history.replace(`/fileDL`);
  };

  const moveMonitorManage = () => {
    history.replace(`/monitor`);
  };

  const moveMailHistory = () => {
    history.replace(`/mailHistory`);
  };

  return (
    <>
      <BackButton />
      <StyledInterviewManagementContainer>
        <StyledHeadline>
          <StyledGroupAndSubjectName>
            <StyledGroupColumn>
              <StatusLabel status={selectedSubjectStatus as Status} />
              <Typography text={selectedGroupName} />
              <Typography text={pageHeader.subjectNumber} />
            </StyledGroupColumn>
            <StyledSubjectNameContainer>
              <InterviewTypeIcon
                interviewType={selectedSubjectType}
                size={24}
              />
              <Typography
                weight={"bold"}
                size="xxlarge"
                text={pageHeader.subjectName}
              />
            </StyledSubjectNameContainer>
          </StyledGroupAndSubjectName>
        </StyledHeadline>
        <StyledContentWrapper>
          <StyledInterviewListContainer
            interviewType={selectedSubjectType as InterviewType}
          >
            <InterviewList
              interviewList={displayInterviewList}
              selectedInterviewDetail={selectedInterviewDetail}
              selectInterviewDetail={selectInterviewDetail}
            />
          </StyledInterviewListContainer>
          <StyledInterviewDetailContainer>
            <StyledButtonRow>
              <StyledButtonRowLeft>
                <TextWithIconButton
                  onClick={moveMonitorManage}
                  text="モニター"
                  variant="white"
                  iconName={TextWithIconTypes.ManageAccounts}
                />
                <TextWithIconButton
                  onClick={moveScenarioManage}
                  text="インタビューフロー"
                  variant="white"
                  iconName={TextWithIconTypes.CardList}
                />
                <TextWithIconButton
                  onClick={moveFileManage}
                  text="インタビュー資料"
                  variant="white"
                  iconName={TextWithIconTypes.Upload}
                />
                <TextWithIconButton
                  onClick={moveFileDL}
                  text="ファイル確認・DL"
                  variant="white"
                  iconName={TextWithIconTypes.Import}
                />
                <TextWithIconButton
                  onClick={downloadExcel}
                  text="エクセルDL"
                  variant="white"
                  iconName={TextWithIconTypes.FileExcel}
                />
                <TextWithIconButton
                  onClick={moveReport}
                  text="レポート出力"
                  variant="white"
                  iconName={TextWithIconTypes.Report}
                />
                {selectedSubjectType === InterviewType.TERM && (
                  <TextWithIconButton
                    onClick={moveMailHistory}
                    text="メール履歴"
                    variant="white"
                    iconName={TextWithIconTypes.Report}
                  />
                )}
              </StyledButtonRowLeft>
              <TextWithIconButton
                onClick={createNewInterview}
                text="実査枠を作成"
                iconName={TextWithIconTypes.Plus}
              />
            </StyledButtonRow>
            <InterviewDetail
              selectInterview={selectInterview}
              selectedInterviewDetail={selectedInterviewDetail}
            />
          </StyledInterviewDetailContainer>
        </StyledContentWrapper>
        {selectedInterview && (
          <InterviewManageModal
            interviewList={displayInterviewList}
            closeHandler={closeSelectedInterview}
            selectedSubjectId={selectedSubjectId}
            selectedInterview={selectedInterview}
            clearSelectedInterview={clearSelectedInterview}
            selectInterviewDetail={selectInterviewDetail}
          />
        )}
      </StyledInterviewManagementContainer>
    </>
  );
};
